// Conversational SEO — client-side execution of the agent's WordPress WRITE tools.
// These reuse the browser's live WPClient + existing helpers. Every write is gated by the user's
// approval in the chat UI (the component calls dispatchWrite only after Approve). Each returns an
// LLM-facing result string.

import type { WPClient } from "./wpClient";
import type { SeoPlugin } from "../types";
import { metaKeysFor, htmlToGutenbergBlocks } from "./fixExecutor";
import { applyElementorEdits, extractElementorBlocks, type ElEdit, type ElFindReplace } from "./elementorContent";

export const WRITE_TOOL_NAMES = new Set([
  "wp_create_page", "wp_update_content", "wp_update_seo_meta", "wp_set_schema", "wp_set_h1", "wp_edit_elementor",
]);

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const arr = (v: unknown): string[] => Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
// Parse the wp_edit_elementor inputs (arrays of small objects) defensively.
const editArr = (v: unknown): ElEdit[] => Array.isArray(v) ? v.flatMap(x => {
  if (x && typeof x === "object") {
    const o = x as Record<string, unknown>;
    const id = str(o.id); const field = str(o.field) || undefined;
    const text = typeof o.text === "string" ? o.text : "";
    if (id) return [{ id, field, text }];
  }
  return [];
}) : [];
const frArr = (v: unknown): ElFindReplace[] => Array.isArray(v) ? v.flatMap(x => {
  if (x && typeof x === "object") {
    const o = x as Record<string, unknown>;
    const find = typeof o.find === "string" ? o.find : "";
    const replace = typeof o.replace === "string" ? o.replace : "";
    if (find) return [{ find, replace }];
  }
  return [];
}) : [];
const escapeHtml = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const stripHtml = (html: string): string => html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const excerpt = (s: string, n = 500): string => (s.length > n ? s.slice(0, n) + "…" : s);

// Structured preview shown on the approval card (built just before the card appears).
export interface ApprovalRow { label: string; value: string }
export interface ApprovalDetail { summary: string; rows: ApprovalRow[]; before?: string; after?: string; preview?: string; warning?: string }

// Human-readable summary shown on the Approve card.
export function describeWrite(name: string, input: Record<string, unknown>): string {
  switch (name) {
    case "wp_create_page": {
      const status = input.status === "publish" ? "publish (LIVE)" : "draft";
      const slug = str(input.slug);
      const type = input.type === "post" ? "post" : "page";
      return `Create a new ${status} ${type}: “${str(input.title) || "(untitled)"}”${slug ? ` at /${slug}/` : ""}.`;
    }
    case "wp_update_content":
      return `Replace the body content of ${input.type === "post" ? "post" : "page"} #${num(input.id) ?? "?"}.`;
    case "wp_update_seo_meta": {
      const bits = [str(input.title) && "meta title", str(input.description) && "meta description"].filter(Boolean).join(" + ");
      return `Update the ${bits || "SEO meta"} of ${input.type === "post" ? "post" : "page"} #${num(input.id) ?? "?"}.`;
    }
    case "wp_set_schema":
      return `Add ${arr(input.blocks).length} JSON-LD schema block(s) to ${input.type === "post" ? "post" : "page"} #${num(input.id) ?? "?"}.`;
    case "wp_edit_elementor": {
      const n = editArr(input.edits).length + frArr(input.findReplace).length;
      return `Edit the Elementor body content of page #${num(input.id) ?? "?"} (${n} text change${n === 1 ? "" : "s"}).`;
    }
    default:
      return `Run ${name}.`;
  }
}

function seoMetaBody(plugin: SeoPlugin, title: string, description: string): Record<string, string> {
  const keys = metaKeysFor(plugin);
  const body: Record<string, string> = {};
  if (keys) { if (title) body[keys.title] = title; if (description) body[keys.description] = description; }
  return body;
}

const ok = (content: string) => ({ ok: true, content });
const fail = (content: string) => ({ ok: false, content });

export async function dispatchWrite(
  name: string,
  input: Record<string, unknown>,
  client: WPClient,
  plugin: SeoPlugin,
): Promise<{ ok: boolean; content: string }> {
  try {
    switch (name) {
      case "wp_create_page": {
        const title = str(input.title);
        const rawHtml = str(input.html);
        if (!title) return fail("Error: title is required.");
        if (!rawHtml) return fail("Error: html content is required.");
        const isPost = input.type === "post";
        const status = input.status === "publish" ? "publish" : "draft";
        const editor = await client.detectEditor();
        const content = editor === "block" ? htmlToGutenbergBlocks(rawHtml) : rawHtml;
        const tagIds = arr(input.tags).length ? await client.getOrCreateTags(arr(input.tags)) : [];
        let parent: number | undefined;
        if (num(input.parentId)) parent = num(input.parentId)!;
        else if (str(input.parentUrl)) parent = (await client.getPageIdByUrl(str(input.parentUrl))) ?? undefined;
        const metaTitle = str(input.metaTitle), metaDescription = str(input.metaDescription);
        const meta = seoMetaBody(plugin, metaTitle, metaDescription);
        const body: Record<string, unknown> = { title, content, status };
        if (str(input.slug)) body.slug = str(input.slug);
        if (tagIds.length) body.tags = tagIds;
        if (parent) body.parent = parent;
        if (Object.keys(meta).length) body.meta = meta;
        const r = isPost ? await client.createPost(body) : await client.createPage(body);
        if (!r.success) return fail(`Error creating ${isPost ? "post" : "page"}: ${r.error ?? "unknown error"}`);
        const d = (r.data ?? {}) as { id?: number; link?: string };
        if (plugin === "aioseo" && d.id && (metaTitle || metaDescription)) {
          try { await client.saveAioseoMeta(d.id, { title: metaTitle || undefined, description: metaDescription || undefined }); } catch { /* non-fatal */ }
        }
        return ok(JSON.stringify({ created: true, id: d.id, url: d.link, status }));
      }

      case "wp_update_content": {
        const id = num(input.id);
        const rawHtml = str(input.html);
        if (!id) return fail("Error: id is required.");
        if (!rawHtml) return fail("Error: html is required.");
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        // Elementor ignores post_content on the front end (it renders from _elementor_data), so a
        // content overwrite here would silently have no visible effect — route the model to the
        // right tool instead of writing something that won't show.
        const bm = await client.getBuilderModel(type, id).catch(() => null);
        if (bm?.builder === "elementor") {
          return fail(`Page #${id} is built with Elementor — replacing post_content won't change what visitors see. Read it with wp_get_page to get its editable blocks, then use wp_edit_elementor to change the body text.`);
        }
        const editor = await client.detectEditor();
        const content = editor === "block" ? htmlToGutenbergBlocks(rawHtml) : rawHtml;
        const r = await client.updateContent(type, id, content);
        return r.success ? ok(JSON.stringify({ updated: true, id, type })) : fail(`Error updating content: ${r.error ?? "unknown error"}`);
      }

      case "wp_edit_elementor": {
        const id = num(input.id);
        if (!id) return fail("Error: id is required (the Elementor page's WordPress id).");
        const edits = editArr(input.edits);
        const findReplace = frArr(input.findReplace);
        if (!edits.length && !findReplace.length) return fail("Error: provide 'edits' (block id/field → new text) and/or 'findReplace' (find/replace pairs).");
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        const model = await client.getBuilderModel(type, id);
        if (model.builder !== "elementor") return fail(`Page #${id} isn't an Elementor page — use wp_update_content to edit it.`);
        if (!Array.isArray(model.elements)) return fail("Error: couldn't read this Elementor page's content tree. Ensure the Local SEO Garage plugin is active on the site so Elementor pages can be edited.");
        const { changed, notFound } = applyElementorEdits(model.elements, edits, findReplace);
        if (!changed) return fail(`No changes were applied${notFound.length ? ` (couldn't find block(s): ${notFound.join(", ")})` : ""}. Re-read the page with wp_get_page to get current block ids/text, or use findReplace.`);
        const r = await client.saveElementorElements(id, model.elements);
        return r.success
          ? ok(JSON.stringify({ updated: true, id, changed, notFound }))
          : fail(`Elementor save failed: ${(r.data as { message?: string })?.message ?? r.error ?? "unknown error"}`);
      }

      case "wp_update_seo_meta": {
        const id = num(input.id);
        if (!id) return fail("Error: id is required.");
        const title = str(input.title), description = str(input.description);
        if (!title && !description) return fail("Error: provide a title and/or description.");
        if (plugin === "aioseo") {
          const r = await client.saveAioseoMeta(id, { title: title || undefined, description: description || undefined });
          return r.success ? ok(JSON.stringify({ updated: true, id, plugin })) : fail(`Error writing AIOSEO meta: ${r.error ?? "unknown error"}`);
        }
        const meta = seoMetaBody(plugin, title, description);
        if (!Object.keys(meta).length) return fail(`The "${plugin}" SEO plugin isn't writable via the REST API.`);
        const singular: "page" | "post" = input.type === "post" ? "post" : "page";
        const r = await client.updatePageOrPost(singular, id, { meta });
        return r.success ? ok(JSON.stringify({ updated: true, id, plugin })) : fail(`Error writing SEO meta: ${r.error ?? "unknown error"}`);
      }

      case "wp_set_schema": {
        const id = num(input.id);
        if (!id) return fail("Error: id is required.");
        const blocks = arr(input.blocks);
        if (!blocks.length) return fail("Error: blocks (JSON-LD strings) are required.");
        // Validate each block is well-formed JSON before writing.
        for (const b of blocks) { try { JSON.parse(b); } catch { return fail("Error: one of the schema blocks isn't valid JSON."); } }
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        const r = await client.setAuditSchema(id, blocks, type);
        return r.success ? ok(JSON.stringify({ schemaAdded: blocks.length, id, type })) : fail(`Error writing schema: ${r.error ?? "unknown error"}`);
      }

      case "wp_set_h1": {
        const id = num(input.id);
        const newH1 = str(input.newH1);
        if (!id) return fail("Error: id is required.");
        if (!newH1) return fail("Error: newH1 is required.");
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        const bm = await client.getBuilderModel(type, id).catch(() => null);
        if (bm?.builder === "elementor") {
          return fail(`Page #${id} is built with Elementor — its H1 lives in a heading widget, not post_content. Read it with wp_get_page to find the heading block, then use wp_edit_elementor to change it.`);
        }
        const cur = await client.getRawContent(type, id);
        if (cur == null) return fail(`Error: couldn't read ${type} #${id}.`);
        if (!/<h1\b[^>]*>[\s\S]*?<\/h1>/i.test(cur)) return fail("Error: no <h1> found in the content — use wp_update_content to add or restructure headings.");
        const updated = cur.replace(/<h1\b([^>]*)>[\s\S]*?<\/h1>/i, `<h1$1>${escapeHtml(newH1)}</h1>`);
        const r = await client.updateContent(type, id, updated);
        return r.success ? ok(JSON.stringify({ h1Updated: true, id, type })) : fail(`Error updating H1: ${r.error ?? "unknown error"}`);
      }
    }
    return fail(`Unknown write tool "${name}".`);
  } catch (e) {
    return fail(`Error: ${e instanceof Error ? e.message : "WordPress write failed"}`);
  }
}

// Build a rich preview for the approval card — fetches current state so the user sees a real
// before→after where possible. Best-effort: any fetch failure just omits that detail.
export async function buildApprovalDetail(
  name: string,
  input: Record<string, unknown>,
  client: WPClient | null,
  plugin: SeoPlugin,
): Promise<ApprovalDetail> {
  const summary = describeWrite(name, input);
  if (!client) return { summary, rows: [] };
  try {
    switch (name) {
      case "wp_create_page": {
        const rows: ApprovalRow[] = [
          { label: "Type", value: input.type === "post" ? "Post" : "Page" },
          { label: "Status", value: input.status === "publish" ? "Publish (LIVE)" : "Draft" },
        ];
        if (str(input.slug)) rows.push({ label: "Slug", value: `/${str(input.slug)}/` });
        if (num(input.parentId)) rows.push({ label: "Parent id", value: String(num(input.parentId)) });
        else if (str(input.parentUrl)) rows.push({ label: "Parent", value: str(input.parentUrl) });
        if (arr(input.tags).length) rows.push({ label: "Tags", value: arr(input.tags).join(", ") });
        if (str(input.metaTitle)) rows.push({ label: "Meta title", value: str(input.metaTitle) });
        if (str(input.metaDescription)) rows.push({ label: "Meta description", value: str(input.metaDescription) });
        let warning: string | undefined;
        try {
          const dup = await client.findExistingByTitleOrSlug({ slug: str(input.slug), title: str(input.title) });
          if (dup) warning = `A ${dup.type} with this ${dup.matchedBy} already exists (${dup.link}). Creating this will make a second one.`;
        } catch { /* ignore */ }
        return { summary, rows, preview: excerpt(stripHtml(str(input.html))), warning };
      }
      case "wp_update_content": {
        const id = num(input.id);
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        let before: string | undefined;
        if (id) { try { const cur = await client.getRawContent(type, id); if (cur != null) before = excerpt(stripHtml(cur)); } catch { /* ignore */ } }
        return { summary, rows: [{ label: "Target", value: `${type === "posts" ? "Post" : "Page"} #${id ?? "?"}` }], before, after: excerpt(stripHtml(str(input.html))) };
      }
      case "wp_update_seo_meta": {
        const id = num(input.id);
        const rows: ApprovalRow[] = [];
        if (plugin === "aioseo" && id) {
          try { const c = await client.getAioseoMeta(id); if (c.title) rows.push({ label: "Current title", value: c.title }); if (c.description) rows.push({ label: "Current description", value: c.description }); } catch { /* ignore */ }
        }
        if (str(input.title)) rows.push({ label: "New meta title", value: str(input.title) });
        if (str(input.description)) rows.push({ label: "New meta description", value: str(input.description) });
        return { summary, rows };
      }
      case "wp_set_schema": {
        const blocks = arr(input.blocks);
        const types = blocks.map(b => { try { const o = JSON.parse(b) as { "@type"?: unknown }; return String(o["@type"] ?? "?"); } catch { return "?"; } });
        // Full, pretty-printed schema (every block) — the approval card shows it collapsed with an
        // expand arrow, so the user can review ALL of it rather than a truncated snippet.
        const pretty = blocks.map(b => { try { return JSON.stringify(JSON.parse(b), null, 2); } catch { return b; } }).join("\n\n");
        return { summary, rows: [{ label: "Schema types", value: types.join(", ") || "(none)" }], preview: pretty };
      }
      case "wp_set_h1": {
        const id = num(input.id);
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        let before: string | undefined;
        if (id) { try { const cur = await client.getRawContent(type, id); const m = cur?.match(/<h1\b[^>]*>([\s\S]*?)<\/h1>/i); if (m) before = stripHtml(m[1]); } catch { /* ignore */ } }
        return { summary, rows: [{ label: "Target", value: `${type === "posts" ? "Post" : "Page"} #${id ?? "?"}` }], before, after: str(input.newH1) };
      }
      case "wp_edit_elementor": {
        const id = num(input.id);
        const type: "pages" | "posts" = input.type === "post" ? "posts" : "pages";
        const edits = editArr(input.edits);
        const findReplace = frArr(input.findReplace);
        const rows: ApprovalRow[] = [{ label: "Target", value: `Elementor page #${id ?? "?"}` }];
        // Best-effort: resolve each targeted block's current text so the card shows a real diff.
        let blocks: ReturnType<typeof extractElementorBlocks> = [];
        if (id) { try { const m = await client.getBuilderModel(type, id); if (Array.isArray(m.elements)) blocks = extractElementorBlocks(m.elements); } catch { /* ignore */ } }
        for (const e of edits) {
          const cur = blocks.find(b => b.id === e.id && (!e.field || b.field === e.field));
          rows.push({ label: `${e.id}${e.field ? `.${e.field}` : ""}`, value: `${cur ? `“${excerpt(cur.text, 80)}” → ` : ""}“${excerpt(e.text, 80)}”` });
        }
        for (const fr of findReplace) rows.push({ label: "Find → Replace", value: `“${excerpt(fr.find, 60)}” → “${excerpt(fr.replace, 60)}”` });
        return { summary, rows };
      }
    }
  } catch { /* fall through to bare summary */ }
  return { summary, rows: [] };
}
